import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PublicAuthLayout, { PublicAuthAlert, PublicAuthSpinner } from '../components/PublicAuthLayout';
import {
  COUNTRY_OPTIONS,
  DEFAULT_PHONE_COUNTRY_ISO,
  formatCountryOption,
  formatPhoneCountryOption,
  getCountryByIso,
  getCountryByName,
  normalizeDialCode,
} from '../data/countries';
import { getCurrentUser, signup } from '../services/api';
import { User } from '../types';

interface SignupProps {
  onSignup: (user: User) => void;
}

const INDUSTRIES = ['Agriculture', 'Energy', 'Facilities', 'Fleet & Logistics', 'Manufacturing', 'Oil & Gas', 'Other'];
const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-1000', '1000+'];
const DEFAULT_PHONE_COUNTRY = getCountryByIso(DEFAULT_PHONE_COUNTRY_ISO) || COUNTRY_OPTIONS[0];

function formatInternationalPhone(rawPhone: string, phoneCountryIso: string) {
  const trimmed = rawPhone.trim();
  if (!trimmed) return '';

  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';

  if (trimmed.startsWith('+')) return `+${digits}`;
  if (digits.startsWith('00')) return `+${digits.slice(2)}`;

  const dialCode = normalizeDialCode(getCountryByIso(phoneCountryIso)?.dialCode || DEFAULT_PHONE_COUNTRY.dialCode);
  const dialDigits = dialCode.replace(/\D/g, '');
  if (dialDigits && digits.startsWith(dialDigits)) return `+${digits}`;

  return `${dialCode}${digits.replace(/^0+/, '')}`;
}

export default function Signup({ onSignup }: SignupProps) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: '',
    full_name: '',
    password: '',
    phone: '',
    phone_country_iso: DEFAULT_PHONE_COUNTRY.iso2,
    org_name: '',
    industry: '',
    country: '',
    company_size: '',
    description: '',
  });
  const [error, setError] = useState('');
  const [accountExists, setAccountExists] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (key: keyof typeof form) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const countryName = event.target.value;
    const selectedCountry = getCountryByName(countryName);
    setForm((prev) => ({
      ...prev,
      country: countryName,
      phone_country_iso: selectedCountry && !prev.phone.trim() ? selectedCountry.iso2 : prev.phone_country_iso,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setAccountExists(false);

    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      const { phone_country_iso, ...signupForm } = form;
      const normalizedPhone = formatInternationalPhone(form.phone, phone_country_iso);
      const formPayload = { ...signupForm, phone: normalizedPhone };
      const payload = Object.fromEntries(Object.entries(formPayload).filter(([, value]) => value.trim() !== '')) as any;
      const data = await signup(payload);
      let user = data.user;

      try {
        user = await getCurrentUser();
      } catch {
        user = {
          ...data.user,
          email_verified_at: null,
          full_name: form.full_name,
          signup_source: 'self_signup',
        };
      }

      sessionStorage.setItem(`pending_verification.${user.id}`, 'true');
      if (data.verification_url) {
        sessionStorage.setItem(`verification_url.${user.id}`, data.verification_url);
      }
      onSignup(user);
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      const res = err.response?.data;
      if (err.response?.status === 409 || res?.code === 'account_exists') {
        setAccountExists(true);
        setError(res?.error || 'An account with this email already exists.');
      } else {
        setError(res?.error || 'Signup failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicAuthLayout
      asideBody="Create a Seedronics workspace for your company, then invite your team, connect devices, and start reviewing dashboards, alerts, reports, and maintenance signals."
      asideTitle="Start with a guided trial."
      eyebrow="Start your workspace"
      footer={
        <>
          Already have access? <Link to="/login">Sign in</Link>
        </>
      }
      highlights={['30-day trial workspace', 'Organization profile', 'Verification email included']}
      title="Create your Seedronics Cloud account."
      subtitle="Tell us who you are and the sites or facilities you want to monitor."
    >
      {error && (
        <PublicAuthAlert>
          <span>{error}</span>
          {accountExists && <Link to="/login">Log in instead</Link>}
        </PublicAuthAlert>
      )}

      <form className="sd-public-auth-form" onSubmit={handleSubmit}>
        <div className="sd-public-auth-grid">
          <label>
            Work email
            <input autoComplete="email" onChange={set('email')} placeholder="you@company.com" required type="email" value={form.email} />
          </label>
          <label>
            Full name
            <input autoComplete="name" onChange={set('full_name')} placeholder="Ada Lovelace" required type="text" value={form.full_name} />
          </label>
          <label>
            Password
            <input autoComplete="new-password" minLength={8} onChange={set('password')} placeholder="Minimum 8 characters" required type="password" value={form.password} />
          </label>
          <label>
            Phone
            <div className="sd-public-phone-field">
              <select
                aria-label="Phone country code"
                autoComplete="tel-country-code"
                onChange={set('phone_country_iso')}
                value={form.phone_country_iso}
              >
                {COUNTRY_OPTIONS.map((country) => (
                  <option key={country.iso2} value={country.iso2}>{formatPhoneCountryOption(country)}</option>
                ))}
              </select>
              <input
                autoComplete="tel-national"
                inputMode="tel"
                onChange={set('phone')}
                placeholder="801 234 5678"
                type="tel"
                value={form.phone}
              />
            </div>
          </label>
        </div>

        <div className="sd-public-auth-divider">Organization profile</div>

        <div className="sd-public-auth-grid">
          <label className="is-wide">
            Organization name
            <input autoComplete="organization" minLength={2} onChange={set('org_name')} placeholder="Company or facility name" required type="text" value={form.org_name} />
          </label>
          <label>
            Industry
            <select onChange={set('industry')} value={form.industry}>
              <option value="">Select industry</option>
              {INDUSTRIES.map((industry) => <option key={industry} value={industry}>{industry}</option>)}
            </select>
          </label>
          <label>
            Country
            <select autoComplete="country-name" onChange={handleCountryChange} value={form.country}>
              <option value="">Select country</option>
              {COUNTRY_OPTIONS.map((country) => (
                <option key={country.iso2} value={country.name}>{formatCountryOption(country)}</option>
              ))}
            </select>
          </label>
          <label>
            Company size
            <select onChange={set('company_size')} value={form.company_size}>
              <option value="">Select size</option>
              {COMPANY_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
            </select>
          </label>
          <label className="is-wide">
            What would you like to monitor?
            <textarea
              onChange={set('description')}
              placeholder="Fuel tanks, generators, site assets, facility runtime, alerts, reports..."
              rows={4}
              value={form.description}
            />
          </label>
        </div>

        <button className="sd-public-auth-submit" disabled={loading} type="submit">
          {loading ? <PublicAuthSpinner /> : 'Create workspace'}
        </button>
      </form>
    </PublicAuthLayout>
  );
}
